import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink, RouterModule, RouterOutlet } from '@angular/router';
import { NgClass } from '@angular/common';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatFormField } from '@angular/material/input';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatDrawer, MatDrawerContainer, MatDrawerContent, MatSidenavModule } from '@angular/material/sidenav';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationResponse } from 'app/layout/layout.types';
import { Client, webpages_Role } from 'app/shared/models/domain/data.model';
import { environment } from 'environments/environment';
import { FuseVerticalNavigationComponent as FuseVerticalNavigationComponent_1 } from '../../../../../@fuse/components/navigation/vertical/vertical.component';
import { FuseFullscreenComponent } from '../../../../../@fuse/components/fullscreen/fullscreen.component';
import { NotificationsComponent } from '../../../common/notifications/notifications.component';
import { UserMenuComponent } from '../../../common/user-menu/user-menu.component';
import { LayoutNavigationEntry, VerticalLayoutBaseComponent } from '../vertical-layout-base.component';

@Component({
    selector: 'dense-layout',
    templateUrl: './dense.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FuseVerticalNavigationComponent_1, RouterLink, MatIconButton, MatIcon, MatFormField, MatSelect, FormsModule, ReactiveFormsModule, MatOption, FuseFullscreenComponent, NotificationsComponent, UserMenuComponent, MatDrawerContainer, MatDrawer, NgClass, MatDrawerContent, RouterOutlet, MatIconModule, MatFormFieldModule, MatSelectModule, MatTooltipModule, MatSidenavModule, RouterModule, MatButtonModule]
})
export class DenseLayoutComponent extends VerticalLayoutBaseComponent implements OnInit {
    @ViewChild('drawer') drawer: MatDrawer;
    isScreenMedium: boolean;
    navigationAppearance: 'default' | 'dense' = 'default';
    isClient: boolean = false;
    isCustomer: boolean = false;
    isAdmin: boolean = false;
    isAuditor: boolean = false;
    isHideHeader: boolean = false;
    clients: Client[];
    formControl: UntypedFormControl = new UntypedFormControl();

    private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

    constructor() {
        super();
        this.authService.localUserRoles?.forEach((role: webpages_Role) => {
            this.isCustomer ||= environment.roleTabs['customer'].includes(role.roleName);
            this.isClient ||= environment.roleTabs['client'].includes(role.roleName);
            this.isAdmin ||= environment.roleTabs['admin'].includes(role.roleName);
            this.isAuditor ||= environment.roleTabs['auditor'].includes(role.roleName);
        });

        if (this.isClient) {
            this.clients = this.authService.localClients;
        }
    }

    override ngOnInit(): void {
        super.ngOnInit();

        if (this.authService.localClientId) {
            this.formControl.setValue(this.authService.localClientId);
        }

        this.formControl?.valueChanges?.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((clientId: number) => {
            this.authService.localClientId = clientId;
            const currentUrl = this.router.url.split('?')[0];
            void this.router.navigate(['/reload'], { replaceUrl: true }).then(() => {
                void this.router.navigate([currentUrl], { replaceUrl: true, queryParams: {} }).then(() => {
                    this.changeDetectorRef.markForCheck();
                });
            });
        });
    }

    toggleNavigationAppearance(): void {
        this.navigationAppearance = (this.navigationAppearance === 'default' ? 'dense' : 'default');
    }

    protected navigationChain(navigation: NavigationResponse): LayoutNavigationEntry[] {
        return [
            { roleKey: 'admin', horizontal: navigation.admin, compact: navigation.adminCompact },
            { roleKey: 'client', horizontal: navigation.client, compact: navigation.client },
            { roleKey: 'mmr', horizontal: navigation.mmr, compact: navigation.mmr },
            { roleKey: 'customer', horizontal: navigation.customer, compact: navigation.customer },
            { roleKey: 'auditor', horizontal: navigation.auditor, compact: navigation.auditor },
            { roleKey: 'salesRep', horizontal: navigation.salesRep, compact: navigation.salesRepCompact, currentRole: 'Sales Rep' }
        ];
    }

    protected override onMediaChange(matchingAliases: string[]): void {
        super.onMediaChange(matchingAliases);
        this.isScreenMedium = !matchingAliases.includes('lg');
    }
}
